import React from 'react';

import * as Sentry from '@sentry/react';
import { BrowserTracing } from '@sentry/tracing';

import './index.css';
import './fonts.css';
import "@cristianlivella/react-scheduler/dist/mormat_react_scheduler.css";
import GenericObject from './typesAdditional/GenericObject';

declare global {
  interface Window {
    RUNTIME_CONFIG: GenericObject;
    _weCrowd: GenericObject,
    weCrowdStorage: {
      items: GenericObject;
      setItem: (key: string, value: any) => void;
      getItem: (key: string) => any;
      removeItem: (key: string) => void;
    }
  }
}

window.weCrowdStorage = {
  items: [],
  setItem: (key: string, value: any) => window.weCrowdStorage.items[key] = value,
  getItem: (key: string) => window.weCrowdStorage.items[key] ?? null,
  removeItem: (key: string) => window.weCrowdStorage.items[key] = undefined
}

window._weCrowd = {
  tenantId: new URLSearchParams(window.location.search).get('wecrowd_tenant_id')
};

export function throwWeCrowdError(payload: GenericObject, sentry = false) {
  const root = document.getElementById('root');
  if (!root) return;

  const base64Payload = JSON.stringify({ ...payload, h: window.location.href });

  const renderErrorPage = (qrCodeFail: boolean) => {
    root.innerHTML = '';

    const container = document.createElement('div');
    // @ts-ignore
    container.style = `
      height: 100%	;
      display: flex;
      flex-direction: column;
      align-items: center;
      justify-content: center;
      font-family: system-ui, -apple-system, sans-serif;
      text-align: center;
      padding: 0 20px;
      background: #f9fafb;
      color: #111827;
    `;

    if (sentry) {
      container.innerHTML = `
      <img
        src="/wecrowd-logo.svg"
        alt="WeCrowd"
        style="height:56px;max-width:220px;object-fit:contain;margin:0 auto 24px;display:block;"
      />
      <h2 style="margin: 10px 0 20px;padding: 0 40px;">Si è verificato un problema tecnico</h2>
      <p style="max-width: 600px; margin-bottom: 0px;">
        Ti invitiamo a riprovare più tardi.
      </p>
      <p style="max-width: 600px; margin-top: 8px; margin-bottom: 20px;">
        Se il problema persiste, contattaci a <a href="mailto:info@wecrowd.it">info@wecrowd.it</a>.
      </p>
      <div id="qr-code" style="margin-top: 20px;"></div>
    `;
    } else {
      container.innerHTML = `
      <img
        src="/wecrowd-logo.svg"
        alt="WeCrowd"
        style="height:56px;max-width:220px;object-fit:contain;margin:0 auto 24px;display:block;"
      />
      <h1 style="font-size: 72px; margin: 0;">404</h1>
      <h2 style="margin: 10px 0 20px;">Pagina non trovata</h2>
      <p style="max-width: 600px; margin-bottom: 0px;">
        L'indirizzo che hai inserito non è valido.<br/>
        Verifica di averlo digitato correttamente.
      </p>
      <p style="max-width: 600px; margin-top: 8px; margin-bottom: 20px;">
        Se il problema persiste, contattaci a <a href="mailto:info@wecrowd.it">info@wecrowd.it</a>.
      </p>
      <div id="qr-code" style="margin-top: 20px;"></div>
    `;
    }

    if (qrCodeFail) {
      const fallback = document.createElement('div');
      fallback.style.cssText = "margin-top: 0px; padding: 14px 16px; border-radius: 12px; background: #fff5f5; border: 1px solid rgba(220, 53, 69, 0.25); font-family: 'SFMono-Regular', Consolas, 'Liberation Mono', Menlo, monospace; font-size: 11px; color: #c0392b; overflow-x: auto; white-space: pre-wrap; word-break: break-word;";
      fallback.textContent = base64Payload;
      container.querySelector('#qr-code')?.appendChild(fallback);
    }

    root.replaceChildren('');
    root.appendChild(container);
  }

  const script = document.createElement('script');
  script.src = 'https://cdn.jsdelivr.net/gh/davidshimjs/qrcodejs/qrcode.min.js';
  script.onload = function () {
    renderErrorPage(false);

    // @ts-ignore
    new QRCode("qr-code", {
      text: base64Payload,
      width: 180,
      height: 180,
      colorDark: "#000000",
      colorLight: "#ffffff",
      // @ts-ignore
      correctLevel: QRCode.CorrectLevel.H
    });
  };
  script.onerror = function () {
    renderErrorPage(true);
  };
  document.head.appendChild(script);
}

async function loadRuntimeConfig() {
  if (process.env.REACT_APP_SENTRY_ENV === 'development') {
    return {
      REACT_APP_API_HOST: "https://api.epuntos-local.devtun.wecrowd.it",
      REACT_APP_SENTRY_ENV: "prod-wcrwd",
      REACT_APP_TENANT: "epuntos",
      REACT_APP_URL: "https://epuntos-local.devtun.wecrowd.it",
      REACT_APP_WECROWD_APP_URL: "https://preview--wecrowd-frontend-alpha.lovable.app",
      wecrowd_config_found: true
    }
  }

  try {
    const res = await fetch('https://api.wecrowd.it/legacy/frontend_config?hostname=' + encodeURI(window.location.hostname));
    if (!res.ok) throw new Error('Unable to load runtime config');
    return res.json();
  } catch {
    throwWeCrowdError({
      err: 'WCRWD-E002',
    }, true);
  }

}

async function bootstrap() {
  const config = await loadRuntimeConfig();

  if (!config.wecrowd_config_found) {
    throwWeCrowdError({
      err: 'WCRWD-E001',
    });

    return;
  }

  window.RUNTIME_CONFIG = config;

  const ReactDOM = await import('react-dom');
  const { default: App } = await import('./App');

  Sentry.init({
    dsn: 'https://bfebcabc920148cabbbce9f2f6945816@o120147.ingest.sentry.io/6340121',
    release: window.RUNTIME_CONFIG.REACT_APP_GIT_SHA ?? '',
    integrations: [new BrowserTracing()],
    tracesSampleRate: 0,
    environment: window.RUNTIME_CONFIG.REACT_APP_SENTRY_ENV,
    tunnel: '/sentun'
  });

  ReactDOM.render(
    <React.StrictMode>
      <App />
    </React.StrictMode>,
    document.getElementById('root')
  );
}

bootstrap();

